import React, { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bell, AlertTriangle, Megaphone, MessageSquare, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";

const KIND_META = {
  note: { color: "#A1A1AA", icon: MessageSquare, label: "Note" },
  update: { color: "#3B82F6", icon: Megaphone, label: "Update" },
  alert: { color: "#F59E0B", icon: Bell, label: "Alert" },
  emergency: { color: "#EF4444", icon: AlertTriangle, label: "Emergency" },
  delay: { color: "#F59E0B", icon: AlertTriangle, label: "Delay" },
};

export default function CommunicationsPage() {
  const [notes, setNotes] = useState([]);
  const [body, setBody] = useState("");
  const [kind, setKind] = useState("note");
  const [filter, setFilter] = useState("");

  const load = async () => {
    const r = await api.get(`/team-notes${filter ? `?kind=${filter}` : ""}`);
    setNotes(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const post = async () => {
    if (!body.trim()) return;
    await api.post("/team-notes", { body, kind });
    setBody("");
    toast.success("Posted to team feed");
    load();
  };

  const del = async (id) => {
    await api.delete(`/team-notes/${id}`);
    load();
  };

  return (
    <div className="p-6 md:p-10 max-w-3xl mx-auto" data-testid="comms-page">
      <div className="mb-8">
        <div className="label-eyebrow">Internal</div>
        <h1 className="font-display text-4xl md:text-5xl mt-2">Team Feed</h1>
        <p className="text-zinc-500 text-sm mt-2">Notes, updates, alerts and emergency notices for the operations crew.</p>
      </div>

      {/* Composer */}
      <div className="card-lux p-5 mb-6">
        <div className="flex gap-2 mb-3 flex-wrap">
          {Object.entries(KIND_META).map(([k, m]) => {
            const Icon = m.icon;
            return (
              <button key={k} onClick={() => setKind(k)} data-testid={`kind-${k}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs border ${kind === k ? "" : "border-[#2a2a2a] text-zinc-500 hover:text-zinc-300"}`}
                style={kind === k ? { background: `${m.color}15`, color: m.color, borderColor: `${m.color}55` } : {}}>
                <Icon className="w-3 h-3" /> {m.label}
              </button>
            );
          })}
        </div>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="What does the team need to know?" data-testid="comp-body"
          className="w-full bg-[#050505] border border-[#2a2a2a] rounded-sm p-3 text-sm focus:outline-none focus:border-[#D4AF37]" />
        <Button onClick={post} data-testid="comp-post" disabled={!body.trim()} className="mt-3 bg-[#D4AF37] text-black hover:bg-[#E5C86B]">
          <Send className="w-3.5 h-3.5 mr-1.5" /> Post
        </Button>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xs text-zinc-500">Filter:</span>
        <button onClick={() => setFilter("")} className={`text-xs px-2 py-1 rounded-sm ${!filter ? "bg-[#D4AF37] text-black" : "text-zinc-500 hover:text-zinc-300"}`}>All</button>
        {Object.entries(KIND_META).map(([k, m]) => (
          <button key={k} onClick={() => setFilter(k)} className={`text-xs px-2 py-1 rounded-sm ${filter === k ? "bg-[#D4AF37] text-black" : "text-zinc-500 hover:text-zinc-300"}`}>{m.label}</button>
        ))}
      </div>

      {/* Feed */}
      <div className="space-y-3">
        {notes.length === 0 && <div className="card-lux p-10 text-center text-zinc-600 text-sm">No notes yet.</div>}
        {notes.map((n) => {
          const meta = KIND_META[n.kind] || KIND_META.note;
          const Icon = meta.icon;
          return (
            <div key={n.id} className="card-lux p-5" data-testid={`note-${n.id}`} style={{ borderColor: `${meta.color}33` }}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0" style={{ background: `${meta.color}15`, color: meta.color }}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="label-eyebrow" style={{ color: meta.color }}>{meta.label}</span>
                    <span className="text-xs text-zinc-500">{n.by_name} · {n.by_role}</span>
                    <span className="text-xs text-zinc-600">· {fmtDate(n.at)}</span>
                  </div>
                  <div className="text-sm text-zinc-200 mt-2 whitespace-pre-wrap">{n.body}</div>
                </div>
                <button onClick={() => del(n.id)} className="text-zinc-600 hover:text-red-400" data-testid={`del-${n.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
